import type { ReactNode } from 'react';

import {
  SEARCH_SECTION_LOAD_MORE_PREFIX,
  SEARCH_SECTION_TYPE_BALANCES,
  SEARCH_SECTION_TYPE_RECENTLY_VIEWED,
  SEARCH_SECTION_TYPE_RESULTS,
  SEARCH_SECTION_TYPE_TRENDING_TOKENS,
} from '@/components/TransactionModule/TokenSelectModal/SearchComboBox/getSections';
import type { SelectTokenFilter } from '@/components/TransactionModule/types';

import type { RecentToken } from '@/store/search';

import type { TokenBase } from '@/utils/0x/token-registry.types';
import { tokenKey } from '@/utils/normalizeAddress';

const TRENDING_TOKENS_DEFAULT_COUNT = 10;
const TOKENS_WITH_BALANCE_DEFAULT_COUNT = 5;
const SHOW_RECENT_RESULTS_COUNT = 4;

/** Token data rendered by the V2 token picker. */
export type TokenSelectToken = TokenBase & { balance?: string; balanceUsd?: string };

/** Section identifiers shared with the legacy combobox. */
export type TokenSelectSectionId =
  | typeof SEARCH_SECTION_TYPE_BALANCES
  | typeof SEARCH_SECTION_TYPE_RECENTLY_VIEWED
  | typeof SEARCH_SECTION_TYPE_RESULTS
  | typeof SEARCH_SECTION_TYPE_TRENDING_TOKENS;

/** A display-ready token-picker section. */
export type TokenSelectSection = {
  id: TokenSelectSectionId;
  title: 'My Tokens' | 'Recent' | 'Results' | 'Trending';
  tokens: TokenSelectToken[];
  /** Renders the "Show more" row at the end of the section. */
  hasMore: boolean;
  /** Results section only: "Show more" renders as a loading row. */
  isLoadingMore?: boolean;
  /** Trending section only: render skeleton rows instead of tokens. */
  isLoading?: boolean;
  /** Recent section only: render the Clear button in the header. */
  clearable?: boolean;
};

/** A typed listbox item resolved without parsing its key. */
export type TokenSelectItem =
  | { kind: 'token'; token: TokenSelectToken; sectionId: TokenSelectSectionId }
  | { kind: 'loadMore'; sectionId: TokenSelectSectionId };

/** Builds the stable listbox key for a token row. */
export const tokenItemKey = (
  token: { chainId: number; address: string },
  sectionId: TokenSelectSectionId,
): string => `${token.chainId}-${token.address}-${sectionId}`;

/** Builds the stable listbox key for a section's load-more row. */
export const loadMoreKey = (sectionId: TokenSelectSectionId): string =>
  `${SEARCH_SECTION_LOAD_MORE_PREFIX}-${sectionId}`;

/** Filters recent tokens for the current view and adds live balances when available. */
export function prepareRecentTokens({
  recentlyViewed,
  selectedChainId,
  searchResults,
  balanceLookup,
}: {
  recentlyViewed: RecentToken[];
  selectedChainId: number | undefined;
  searchResults: TokenSelectToken[] | undefined;
  balanceLookup: Map<string, { balance: string; balanceUsd: string }> | undefined;
}): TokenSelectToken[] {
  const visibleRecentTokens = recentlyViewed.filter(
    (token) => selectedChainId === undefined || token.chainId === selectedChainId,
  );

  if (searchResults !== undefined) {
    const searchResultLookup = new Map(
      searchResults.map((token) => [tokenKey(token.address, token.chainId), token]),
    );

    return visibleRecentTokens.flatMap((token) => {
      const key = tokenKey(token.address, token.chainId);
      const matched = searchResultLookup.get(key);
      if (!matched) return [];
      if (matched.balance && matched.balanceUsd) {
        return [{ ...token, balance: matched.balance, balanceUsd: matched.balanceUsd }];
      }
      const liveBalance = balanceLookup?.get(key);
      return [{ ...token, ...liveBalance }];
    });
  }

  return visibleRecentTokens.map((token) => ({
    ...token,
    ...balanceLookup?.get(tokenKey(token.address, token.chainId)),
  }));
}

/** Applies the legacy section ordering, caps, and loading states to token data. */
export function buildTokenSelectSections({
  searchResults,
  trendingTokens,
  tokensWithBalances,
  recentTokens,
  showAllBalances,
  showAllTrending,
  hasMoreSearchResults,
  isLoadingMoreSearchResults,
}: {
  searchResults: TokenSelectToken[] | undefined;
  trendingTokens: TokenSelectToken[] | undefined;
  tokensWithBalances: TokenSelectToken[] | undefined;
  recentTokens: TokenSelectToken[];
  showAllBalances: boolean;
  showAllTrending: boolean;
  hasMoreSearchResults: boolean;
  isLoadingMoreSearchResults: boolean;
}): TokenSelectSection[] {
  const sections: TokenSelectSection[] = [];

  if (searchResults === undefined && tokensWithBalances?.length) {
    sections.push({
      id: SEARCH_SECTION_TYPE_BALANCES,
      title: 'My Tokens',
      tokens: showAllBalances
        ? tokensWithBalances
        : tokensWithBalances.slice(0, TOKENS_WITH_BALANCE_DEFAULT_COUNT),
      hasMore: !showAllBalances && tokensWithBalances.length > TOKENS_WITH_BALANCE_DEFAULT_COUNT,
    });
  }

  if (recentTokens.length) {
    sections.push({
      id: SEARCH_SECTION_TYPE_RECENTLY_VIEWED,
      title: 'Recent',
      tokens: recentTokens.slice(0, SHOW_RECENT_RESULTS_COUNT),
      hasMore: false,
      clearable: true,
    });
  }

  if (searchResults !== undefined) {
    sections.push({
      id: SEARCH_SECTION_TYPE_RESULTS,
      title: 'Results',
      tokens: searchResults,
      hasMore: hasMoreSearchResults,
      isLoadingMore: isLoadingMoreSearchResults,
    });
  } else if (trendingTokens !== undefined) {
    sections.push({
      id: SEARCH_SECTION_TYPE_TRENDING_TOKENS,
      title: 'Trending',
      tokens: showAllTrending
        ? trendingTokens
        : trendingTokens.slice(0, TRENDING_TOKENS_DEFAULT_COUNT),
      hasMore: !showAllTrending && trendingTokens.length > TRENDING_TOKENS_DEFAULT_COUNT,
    });
  } else {
    sections.push({
      id: SEARCH_SECTION_TYPE_TRENDING_TOKENS,
      title: 'Trending',
      tokens: [],
      hasMore: false,
      isLoading: true,
    });
  }

  return sections;
}

/** Indexes visible listbox keys to typed token or load-more actions. */
export function buildItemIndex(sections: TokenSelectSection[]): Map<string, TokenSelectItem> {
  const index = new Map<string, TokenSelectItem>();

  for (const section of sections) {
    for (const token of section.tokens) {
      index.set(tokenItemKey(token, section.id), {
        kind: 'token',
        token,
        sectionId: section.id,
      });
    }
    if (section.hasMore) {
      index.set(loadMoreKey(section.id), { kind: 'loadMore', sectionId: section.id });
    }
  }

  return index;
}

/** Computes listbox disabled keys and optional token-level reasons. */
export function getDisabledState(
  sections: TokenSelectSection[],
  filterToken: SelectTokenFilter | undefined,
): { disabledKeys: string[] | undefined; disabledReasons: Map<string, ReactNode> | undefined } {
  if (!filterToken) return { disabledKeys: undefined, disabledReasons: undefined };

  const disabledKeys: string[] = [];
  const disabledReasons = new Map<string, ReactNode>();

  for (const section of sections) {
    for (const token of section.tokens) {
      const result = filterToken(token);
      const selectable = typeof result === 'boolean' ? result : result.selectable;
      if (selectable) continue;

      disabledKeys.push(tokenItemKey(token, section.id));
      if (typeof result === 'object') {
        disabledReasons.set(`${token.chainId}-${token.address}`, result.reason);
      }
    }
  }

  return { disabledKeys, disabledReasons };
}
